import {
    Component,
    EventEmitter,
    Input,
    Output,
    ViewChild,
    ElementRef,
    SimpleChanges,
    OnInit,
    OnChanges,
    OnDestroy
} from "@angular/core";
import {
    ITCIMediaAsset,
    TCI_MEDIA_DISPLAY_MODE,
    TCIMediaDisplaySize,
    supportedMediaImageSpecs,
    ITCISupportedMediaImageSpecs,
    mediaMimeType,
    MAX_ASSET_UPLOAD_ALLOWED,
    MAX_UPLOAD_MB_ALLOWED,
    TCI_MEDIA_EDIT_MODES,
    ITCIMediaAssetMap
} from "@tci/common";
import { UploadEvent } from "ngx-file-drop";
import * as lodash from "lodash";
import { Observable, Subscription, Observer } from "rxjs";
import { FormControl } from "@angular/forms";
import { Http, ResponseContentType } from "@angular/http";
import { BoxAlignment } from "../ng-popup/ng-popup.component";
import { NgMediaService } from "./ng-media.service";


const ENCODING_STR = "Binary";

export enum mediaType {
    VIDEO = "video",
    IMAGE = "image"
}

@Component({
    selector: "ng-media",
    templateUrl: "./ng-media.component.html",
    styleUrls: ["./ng-media.component.less"]
})
export class NgMediaComponent implements OnInit, OnChanges, OnDestroy {

    @Input() mediaAssets: ITCIMediaAsset[] = [];

    @Input() autoplay: boolean;

    @Input() displaySize: TCIMediaDisplaySize;

    @Input() mode: TCI_MEDIA_DISPLAY_MODE;

    @Input() editMode: TCI_MEDIA_EDIT_MODES;

    @Input() refUrl: string;

    @Input() cssClasses: string | string[] = [];

    /**
     * Implement this to notify the parent to do any subscription or pre-init activity
     * usage: (onBeforeShow)="doBeforeShow()"
     */
    @Output() beforeShow: EventEmitter<ITCIMediaAsset[]> = new EventEmitter<ITCIMediaAsset[]>();
    /**
     * Implement this to notify the parent to do clean up subscription before hiding
     * (onAfterHide)="doAfterHide()"
     */
    @Output() afterHide: EventEmitter<ITCIMediaAsset[]> = new EventEmitter<ITCIMediaAsset[]>();

    @ViewChild("ngMediaRoot") rootElement: ElementRef;
    @ViewChild("inputFiles") FilesInput: ElementRef;

    readonly maxAssetUploadAllowed = MAX_ASSET_UPLOAD_ALLOWED;
    readonly maxUploadMBAllowed = MAX_UPLOAD_MB_ALLOWED;

    isAddMediaView = true;
    isAddImageView = false;
    isAddVideoView = false;
    isOpenPopup = false;
    droppedFiles: ITCIMediaAssetMap = {};
    droppedFilesKeys: string[];
    droppedFilesValues: ITCIMediaAsset[];
    allowedImageSpecs: ITCISupportedMediaImageSpecs[] = [];
    allowedImageSpecsInput = "";
    allowedToAttach = false;
    numberOfUploadsLeft = 5;
    isFileValid = false;
    subscriptions: Subscription[] = [];
    videoURL = "";
    postData: ITCIMediaAsset[] = [];
    videoURLCtrl = new FormControl();
    errorMsg = "";
    alignment = BoxAlignment.TOP_CENTER;

    constructor(private mediaSvc: NgMediaService) {
    }

    ngOnInit() {
        this.getallowedImageSpecs();
        /**
         * Incoming mediaAssets in TCI_MEDIA_EDIT_MODES.EDIT_CREATE mode will always be empty
         */
        if (lodash.isArray(this.mediaAssets) && this.editMode === TCI_MEDIA_EDIT_MODES.EDIT_UPDATE) {
            this.initEditMode();
        }
    }

    ngOnChanges(changes: SimpleChanges) {
        if (changes.mediaAssets && changes.editMode) {
            this.initEditMode();
        }
    }
    /**
     * In EDIT_UPDATE mode, getting base64 data of every image media asset using mediaUrl coming from backend.
     * This is to ensure consistency for WS backend when user updates one or more media asset in edit
     * listing action and also between ng-media output in both EDIT_UPDATE and EDIT_CREATE modes.
     */
    initEditMode() {
        const assetObs: Observable<ITCIMediaAsset>[] = this.mediaAssets.map(asset => Observable.create((observer: Observer<any>) => {
            if (asset.mimeType.includes(mediaType.IMAGE)) {
                this.subscriptions.push(this.mediaSvc.getBase64FromUrl(asset.mediaUrl).subscribe(dataUrl => {
                    const res: ITCIMediaAsset = {
                        content: dataUrl,
                        encoding: ENCODING_STR,
                        mimeType: mediaMimeType.IMAGE,
                        mediaUrl: ""
                    };
                    observer.next(res);
                    observer.complete();
                }));
            } else {
                observer.next(asset);
                observer.complete();
            }
        }));
        this.subscriptions.push(Observable.concat(...assetObs)
        .toArray()
        .finally(() => this.processSlots())
        .subscribe(assets => {
            this.postData = assets;
            this.doBeforeShow();
        }));
    }

    getallowedImageSpecs() {
        this.allowedImageSpecs = supportedMediaImageSpecs();
        this.allowedImageSpecs.forEach(spec => this.allowedImageSpecsInput += `.${spec.extension},`);
    }

    showOverlay(event) {
        this.showAddMediaView();
        this.resetFiles();
        this.resetVideoCtrl();
        this.isOpenPopup = !this.isOpenPopup;
    }

    hideOverlay() {
        this.isOpenPopup = !this.isOpenPopup;
    }

    doBeforeShow() {
        this.beforeShow.emit(this.postData);
    }

    doAfterHide() {
        this.afterHide.emit(this.postData);
    }

    addImgView() {
        this.allowedToAttach = false;
        this.showAddImgView();
    }

    addVideoView() {
        this.resetVideoCtrl();
        this.allowedToAttach = false;
        this.showAddVideoView();
    }

    editVideoUrl(event) {
        this.allowedToAttach = true;
        this.showAddVideoView();
    }

    clickOutside() {
        /**
         * IPAS-17040 - Add media dialog has no close button or any other way to exit from it than clicking on add media again.
         * If user has selected an image or video field is dirty, then dont hide the popup on click outside.
         */
        if (Object.keys(this.droppedFiles).length > 0 || this.videoURLCtrl.dirty) {
            return;
        }
        this.hideOverlay();
    }

    handleVideoURL() {
        if (this.videoURLCtrl.dirty) {
            this.errorMsg = !this.isUrlValid(this.videoURL) && "Please enter a valid URL" || "";
            if (this.videoURL.indexOf("youtube.com") !== -1 || this.videoURL.indexOf("youtu.be") !== -1) {
                this.isYTUrlValid(this.videoURL);
            }
            if (!this.errorMsg) {
                this.videoURLCtrl.markAsDirty();
                this.allowedToAttach = true;
            } else {
                this.allowedToAttach = false;
            }
        }
    }

    isUrlValid(url: string): boolean {
        const res = url.match(/(http(s)?:\/\/.)?(www\.)?[-a-zA-Z0-9@:%._\+~#=]{2,256}\.[a-z]{2,6}\b([-a-zA-Z0-9@:%_\+.~#?&//=]*)/g);
        return res ? true : false;
    }

    isYTUrlValid(url: string) {
        const p = /^(?:https?:\/\/)?(?:www\.)?(?:youtu\.be\/|youtube\.com\/(?:embed\/|v\/|watch\?v=|watch\?.+&v=))((\w|-){11})(?:\S+)?$/;
        const matches = url.match(p);
        if (!matches) {
            this.errorMsg = "Please enter a valid Youtube URL";
        }
    }

    cancel() {
        this.hideOverlay();
    }

    attachMedia() {
        if (this.isAddImageView) {
            Object.values(this.droppedFiles).forEach(fr => {
                if (this.postData.length < this.maxAssetUploadAllowed) {
                    this.postData.push(fr);
                }
            });
        } else if (this.isAddVideoView) {
            const videoAsset = <ITCIMediaAsset>{
                content: null,
                mediaUrl: this.videoURL,
                encoding: null,
                mimeType: mediaMimeType.VIDEO
            };
            this.postData.push(videoAsset);
            this.resetVideoCtrl();
        }
        this.processSlots();
        this.hideOverlay();
        this.doAfterHide();
    }

    showAddMediaView() {
        this.isAddMediaView = true;
        this.isAddImageView = this.isAddVideoView = false;
    }

    showAddImgView() {
        this.isAddImageView = true;
        this.isAddMediaView = this.isAddVideoView = false;
    }

    showAddVideoView() {
        this.isAddVideoView = true;
        this.isAddMediaView = this.isAddImageView = false;
    }

    dropped(event: UploadEvent) {
        const files: File[] = [];
        let processed = 0;
        event.files
            .filter(f => f.fileEntry.isFile)
            .forEach(f => {
                f.fileEntry.file((fe: File) => {
                    files.push(fe);
                    processed++;
                    if (processed === event.files.length) {
                        this.handleSelectedFiles(files);
                    }
                });
            });
    }

    handleSelectedFiles(files: File[]) {
        // since input file HTML element returns an object and file-drop returns an array.
        if (!Array.isArray(files)) {
            files = <File[]>lodash.toArray(files);
        }
        const emitErr = (msg: string) => {
            console.log(msg);
            this.errorMsg = msg;
            if (!(Object.values(this.droppedFiles).length > 0)) {
                this.allowedToAttach = false;
            }
        };
        const allowedUpload = (Object.values(this.droppedFiles).length + files.length) <= this.numberOfUploadsLeft;
        if (files.length === 0) {
            return;
        } else if (!allowedUpload) {
            emitErr("There are more selected files than expected.");
        } else if (!this.allowedImageSpecs.some(a => files.map(f => f.name.toLowerCase()).some(f => f.endsWith("." + a.extension)))) {
            emitErr("There are unsupported files in your selection.");
        } else if (this.validateSize(files)) {
            emitErr(`There are files larger than ${this.maxUploadMBAllowed}MB in your selection.`);
        } else {
            const fileCheck: Observable<ITCIMediaAssetMap>[] = files.map(file => Observable.create((observer: Observer<any>) => {
                this.subscriptions.push(this.mediaSvc.getBase64FromBlob(file).subscribe(dataUrl => {
                    const res: ITCIMediaAsset = {
                        content: dataUrl,
                        encoding: ENCODING_STR,
                        mimeType: file.type,
                        mediaUrl: ""
                    };
                    /**
                     * Using MediaAssetMap to store fileName and display in Html template
                     */
                    const assetMap: ITCIMediaAssetMap = {
                        [file.name]: res
                    };
                    observer.next(assetMap);
                    observer.complete();
                }));
            }));
            this.subscriptions.push(
                Observable.merge(...fileCheck)
                    .toArray()
                    .subscribe((DroppedFiles: ITCIMediaAssetMap[]) => {
                        DroppedFiles.forEach(fr => {
                            this.droppedFiles[Object.keys(fr)[0]] = Object.values(fr)[0];
                            this.updateDroppedFilesKeyValues();
                        });
                        this.allowedToAttach = this.isFileValid = true;
                        this.errorMsg = "";
                    })
            );
        }
    }

    updateDroppedFilesKeyValues() {
        this.droppedFilesKeys = Object.keys(this.droppedFiles);
        this.droppedFilesValues = Object.values(this.droppedFiles);
    }

    /**
     * call this method everytime after adding/removing elements from postData array.
     */
    processSlots() {
        this.numberOfUploadsLeft = this.maxAssetUploadAllowed - this.postData.length;
    }

    /**
     * Current limit of file size is 1 MB
     */
    validateSize(files: File[]): boolean {
        let sizeFlag = false;
        const len = files.length;
        for (let i = 0; i < len; i++) {
            const s = parseFloat((files[i].size / (1024 * 1024)).toFixed(2)); // allows upto 0.9 MB
            if (s > this.maxUploadMBAllowed) {
                sizeFlag = true;
                break;
            }
        }
        return sizeFlag;
    }

    removeThumb(index) {
        const asset = this.postData[index];
        this.postData.splice(index, 1);
        if (asset.mimeType.includes(mediaType.VIDEO)) {
            this.resetVideoCtrl();
        }
        this.processSlots();
        this.doAfterHide();
    }

    resetFiles() {
        this.droppedFiles = {};
        this.updateDroppedFilesKeyValues();
        this.allowedToAttach = false;
        this.isFileValid = false;
        this.errorMsg = "";
    }

    resetFile(key) {
        delete this.droppedFiles[key];
        this.updateDroppedFilesKeyValues();
        if (Object.values(this.droppedFiles).length === 0) {
            this.allowedToAttach = false;
            this.isFileValid = false;
            this.errorMsg = "";
        }
    }

    resetVideoCtrl() {
        this.videoURLCtrl.markAsPristine();
        this.videoURL = "";
    }

    ngOnDestroy() {
        this.subscriptions.forEach(sub => sub.unsubscribe());
    }
}
